import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'syngenta-digital-cropwise-react-ui-kit';
import { Select, RefSelectProps } from 'antd';
import SearchIcon from './SearchIcon';
import CreateSvg from 'assets/create_24px.svg?react';
import { useTranslation } from 'react-i18next';
import { useResourceMapperStore } from '../store/useResourceMapperStore';
import { MasterProduct } from '../types';
import ClearIcon from './ClearIcon';
import { OptionContent } from './OptionContent';
import { EmptyContent } from './EmptyContent';
import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from '../../../services/amplitudeHelper';
import './ResourceMasterListSelect.less';

type CropwiseResourceSelectProps = {
  handleClickCreateNewField: () => void;
  dropdownClassName: string;
  selectedUnmappedProductDescription: string | undefined;
  selectedIntegration?: string;
};

const { Option, OptGroup } = Select;

const ResourceMasterListSelect = ({
  handleClickCreateNewField,
  dropdownClassName,
  selectedUnmappedProductDescription,
  selectedIntegration,
}: CropwiseResourceSelectProps) => {
  const [isOpen, setOpen] = useState<boolean>();
  const [suggestedProducts, setSuggestedProducts] = useState<MasterProduct[]>();
  const myRef = useRef<RefSelectProps>(null);

  const {
    productsMasterList,
    isLoadingProductsMasterList,
    activeResourceType,
    setSelectedMasterProductId,
    selectedMasterProductId,
    getFilteredProductsMasterList,
    setSearchString,
    searchString,
    findTopMatches,
  } = useResourceMapperStore();

  const { t } = useTranslation();

  const trackEvent = useAmplitude();

  useEffect(() => {
    if (!selectedMasterProductId && myRef.current && isOpen) {
      myRef.current.scrollTo(0);
    }
  }, [selectedMasterProductId, isOpen]);

  const generateCWResourceSuggestionsOptions = useMemo(() => {
    if (!selectedUnmappedProductDescription || searchString) return;

    const suggestions: MasterProduct[] = findTopMatches(selectedUnmappedProductDescription, 3);
    setSuggestedProducts(suggestions);
    return suggestions?.map((product: any, index: number) => (
      <Option
        data-cy={`cropwise_select_option_suggestion_${product.product_name}`}
        key={`suggestion_${product.product_id}`}
        className={`py-6 px-15 m-0 h-58px border-b-1 border-neutral-20 ${
          index === 2 && 'last-suggestion-item'
        }`}
        value={product.product_id}
      >
        <OptionContent product={product} />
      </Option>
    ));
  }, [selectedUnmappedProductDescription, findTopMatches, searchString]);

  const generateCWResourceOptions = useMemo(() => {
    if (!productsMasterList?.length) return null;

    const optGroupLabel = t('timeline-analytics.suggested');

    const optionList = getFilteredProductsMasterList(searchString).map((product: MasterProduct) => (
      <Option
        data-cy={`cropwise_select_option_${product.product_name}`}
        key={product.product_id}
        value={product.product_id}
        className={'py-6 px-15 m-0 h-58px border-b-1 border-neutral-20'}
      >
        <OptionContent product={product} />
      </Option>
    ));

    if (searchString) {
      return optionList;
    }

    return [
      <OptGroup key="suggested" label={optGroupLabel}>
        {generateCWResourceSuggestionsOptions}
      </OptGroup>,
      ...optionList,
    ];
  }, [
    productsMasterList,
    t,
    getFilteredProductsMasterList,
    searchString,
    generateCWResourceSuggestionsOptions,
  ]);

  const handleOnSearch = useCallback(
    (event: any) => setSearchString(event.target.value),
    [setSearchString],
  );

  const trackSelectProductCatalog = useCallback(
    (selectedProductId: string) =>
      trackEvent(AmplitudeEventEnum.SELECT_CATALOG_PRODUCT, {
        [AmplitudePropertyKeyEnum.INTEGRATION_NAME]: selectedIntegration,
        [AmplitudePropertyKeyEnum.SUGGESTED_PRODUCT]: !!suggestedProducts?.find(
          (p) => p.product_id === selectedProductId,
        ),
      }),
    [selectedIntegration, trackEvent, suggestedProducts],
  );

  const handleOnChange = useCallback(
    (value: string) => {
      setSelectedMasterProductId(value);
      trackSelectProductCatalog(value);
    },
    [setSelectedMasterProductId, trackSelectProductCatalog],
  );

  const selectPlaceholder = t(`Select ${activeResourceType?.toLowerCase() ?? 'product'}`);
  const inputPlaceholder = t('file-upload.input_search');

  return (
    <Select
      ref={myRef}
      className="cropwise_resource_select"
      data-cy="cropwise_resource_select"
      style={{ width: '100%' }}
      value={selectedMasterProductId}
      onChange={handleOnChange}
      placeholder={selectPlaceholder}
      loading={isLoadingProductsMasterList}
      dropdownClassName={dropdownClassName}
      disabled={isLoadingProductsMasterList}
      onDropdownVisibleChange={setOpen}
      status={!selectedMasterProductId ? 'error' : ''}
      dropdownRender={(menu: any) => {
        return (
          <div className={!searchString ? 'filter-inactive' : ''} data-testid="dropdown-content">
            <Input
              data-cy="cropwise_resource_input"
              prefix={<SearchIcon />}
              suffix={
                <button onClick={() => setSearchString('')}>
                  <ClearIcon />
                </button>
              }
              placeholder={inputPlaceholder}
              value={searchString}
              onChange={handleOnSearch}
              style={{
                width: '100%',
                margin: '0px',
                background: '#F3F4F6',
                borderColor: '#C2C7D0',
              }}
            />
            {activeResourceType === 'FIELD' && (
              <div className="mb-5">
                <div className="ml-12 mb-8 flex flex-row items-center">
                  <CreateSvg />
                  <a
                    href="#!"
                    data-cy="create_new_field_link"
                    onClick={handleClickCreateNewField}
                    className="ml-8 text-green-70"
                  >
                    {t('resource-mapper.create_field')}
                  </a>
                </div>
                <hr className="border-neutral-20" />
              </div>
            )}
            {menu}
          </div>
        );
      }}
      notFoundContent={<EmptyContent />}
    >
      {generateCWResourceOptions}
    </Select>
  );
};

export default memo(ResourceMasterListSelect);
